import { createFeatureSelector, createSelector } from '@ngrx/store'
import { InitializeState } from '../state/initial.state'
import { GlobalConstants } from '../../constants/global-constants'
import { LocationAPIModel } from '../../models/api.model'

const getInitializeState = createFeatureSelector<InitializeState>('initialize')

export const getTaskTypesFromState = createSelector(getInitializeState, state => state.taskTypes)

export const getFilteredTaskTypesByModuleAndCategoryAndSubCategoryFromState = (
  module: string,
  category: string,
  subCategory: string
) =>
  createSelector(getTaskTypesFromState, getUserTypeFromState, (taskTypesFromState, userType) => {
    if (!taskTypesFromState || !userType) {
      return []
    }

    const taskTypes =
      taskTypesFromState &&
      taskTypesFromState[category] &&
      taskTypesFromState[category][subCategory] &&
      taskTypesFromState[category][subCategory][module]
        ? taskTypesFromState[category][subCategory][module]
        : []

    return taskTypes?.filter(task => {
      if (userType === GlobalConstants.USER_TYPE.EMPLOYEE_ADMIN) {
        return task && task.creatableByEmployer
      } else if (userType === GlobalConstants.USER_TYPE.RMS_ADMIN) {
        return task && task.creatableByRMS
      }
      return false
    })
  })

export const getUserNameFromState = createSelector(getInitializeState, state => state?.user?.fullName)
export const getUserTypeFromState = createSelector(getInitializeState, state => state?.user?.userType)
export const getUserFromState = createSelector(getInitializeState, state => state?.user)
export const getSelectedCompaniesFromState = createSelector(getInitializeState, state => state?.selectedCompanies)
export const getRelatedCompaniesFromState = createSelector(getInitializeState, state => state?.relatedCompanies)
export const getUIStatusMappingFromState = createSelector(getInitializeState, state => state?.uiStatusMapping)
export const getStatusListFromState = (categoryName: string, moduleName: string, code: string) =>
  createSelector(getInitializeState, state => state?.taskTypes?.[categoryName]?.[moduleName]?.[code]?.statusList)

export const getUniqueNextActionFromTaskTypes = (moduleName: string) =>
  createSelector(getTaskTypesFromState, tasks => {
    let nextActionsMap = new Map<string, string>()
    if (tasks) {
      for (let [category, subCategoryData] of Object.entries(tasks)) {
        for (const subCategory of Object.keys(subCategoryData)) {
          const moduleData = subCategoryData[subCategory]
          if (moduleName && moduleData?.hasOwnProperty(moduleName)) {
            const taskTypes = moduleData[moduleName]
            if (taskTypes) {
              nextActionsMap = taskTypes.reduce((nextActionsMap, task) => {
                if (task.statusList) {
                  task.statusList.forEach(status => {
                    const nextAction = status?.nextActionToBeTaken
                    if (nextAction !== null && !nextActionsMap.has(nextAction)) {
                      nextActionsMap.set(nextAction, nextAction)
                    }
                  })
                }
                return nextActionsMap
              }, new Map<string, string>())
            }
          }
        }
      }
    }
    return nextActionsMap
  })

export const getStatusMapFromTaskTypes = (moduleName: string) =>
  createSelector(getTaskTypesFromState, tasks => {
    let statusMap = new Map<string, string>()
    if (tasks) {
      for (let [category, subCategoryData] of Object.entries(tasks)) {
        for (const subCategory of Object.keys(subCategoryData)) {
          const moduleData = subCategoryData[subCategory]
          if (moduleName && moduleData?.hasOwnProperty(moduleName)) {
            const taskTypes = moduleData[moduleName]
            if (taskTypes) {
              statusMap = taskTypes.reduce((statusMap, task) => {
                if (task.statusList) {
                  task.statusList.forEach(status => {
                    const code = status?.code
                    if (code && !statusMap.has(code)) {
                      statusMap.set(code, status.label)
                    }
                  })
                }
                return statusMap
              }, new Map<string, string>())
            }
          }
        }
      }
    }
    return statusMap
  })

export const getTaskCategorySubCategoryMapFromState = (module: string) =>
  createSelector(getTaskTypesFromState, state => {
    const taskTypes = state
    const categorySubcategoryMap = new Map<string, string[]>()

    if (taskTypes) {
      for (let [category, subCategoryData] of Object.entries(taskTypes)) {
        let subCategoryArr: string[] = []
        for (const subCategory of Object.keys(subCategoryData)) {
          if (module != 'NA') {
            const moduleData = subCategoryData[subCategory]
            if (moduleData.hasOwnProperty(module)) {
              subCategoryArr.push(subCategory)
            }
          } else {
            //custom categorization for knowledge article
            // Labour and Other LLC reduced to single category as LLC
            category = category.replace(GlobalConstants.CATEGORY_TYPE.OTHER_LLC, GlobalConstants.CATEGORY_TYPE.LLC)
            if (subCategory) {
              category =
                subCategory === GlobalConstants.CATEGORY_TYPE.LABOUR ? GlobalConstants.CATEGORY_TYPE.LLC : category
            }
            if (!subCategoryArr.includes(subCategory)) {
              subCategoryArr.push(subCategory)
            }
            if (category === GlobalConstants.CATEGORY_TYPE.LLC) {
              let existingLLCSubCategories = categorySubcategoryMap.get(category) || []
              const combinedSubCategories = new Set([...subCategoryArr, ...existingLLCSubCategories])
              subCategoryArr = Array.from(combinedSubCategories)
            }
          }
        }
        if (subCategoryArr.length > 0) {
          categorySubcategoryMap.set(category, subCategoryArr)
        }
      }
    }
    return categorySubcategoryMap
  })

export const selectRelatedLocationMap = createSelector(getInitializeState, (state: InitializeState) => {
  const relatedLocationMap = new Map<string, LocationAPIModel[]>()
  state?.relatedLocations?.forEach(location => {
    const { companyEntityId } = location
    if (relatedLocationMap.has(companyEntityId)) {
      relatedLocationMap.get(companyEntityId)?.push(location)
    } else {
      relatedLocationMap.set(companyEntityId, [location])
    }
  })
  return relatedLocationMap
})

export const getOptionsFromState = createSelector(getInitializeState, state => state.options)

export const getScreenConfigFromState = (type: string) =>
  createSelector(getInitializeState, state => state?.screenConfigs?.find(s => s.type === type))
